import OrderOptimizerDetailsOther, {
  OrderOptimizerDetailsOtherDriver,
  OrderOptimizerDetailsOtherOptimizedStats,
  OrderOptimizerDetailsOtherOrders,
  OrderOptimizerDetailsOtherOrdersByDriverAba,
  OrderOptimizerDetailsOtherOrdersByDriverSabs,
  OrderOptimizerDetailsOtherStatus,
  OrderOptimizerDetailsOtherSteps,
  OrderOptimizerDetailsOtherStepsEndToHome,
  OrderOptimizerDetailsOtherStepsHomeToStart,
  OrderOptimizerDetailsOtherStepsOrders,
  OrderOptimizerDetailsOtherStepsOrdersPoints,
  OrderOptimizerDetailsOtherUnoptimizedStats,
} from "../../../../../common/services/order-optimizer/details/other/order-optimizer-details-other";
import OrderOptimizerTaxiDetailsItem, {
  OrderOptimizerTaxiDetailsItemDriver,
  OrderOptimizerTaxiDetailsItemOptimizedStats,
  OrderOptimizerTaxiDetailsItemOrders,
  OrderOptimizerTaxiDetailsItemOrdersByDriverAba,
  OrderOptimizerTaxiDetailsItemOrdersByDriverSabs,
  OrderOptimizerTaxiDetailsItemStatus,
  OrderOptimizerTaxiDetailsItemSteps,
  OrderOptimizerTaxiDetailsItemStepsEndToHome,
  OrderOptimizerTaxiDetailsItemStepsHomeToStart,
  OrderOptimizerTaxiDetailsItemStepsOrders,
  OrderOptimizerTaxiDetailsItemStepsOrdersPoints,
  OrderOptimizerTaxiDetailsItemUnoptimizedStats,
} from "./types/order-optimizer-taxi-details-item";

const createDetailsItemStepsOrderPoint = (
  stepsOrderPoint: OrderOptimizerDetailsOtherStepsOrdersPoints,
): OrderOptimizerTaxiDetailsItemStepsOrdersPoints => ({
  displayName: stepsOrderPoint.displayName,
  distance: stepsOrderPoint.distance,
  driverDistance: stepsOrderPoint.driverDistance,
  driverDrivingTime: stepsOrderPoint.driverDrivingTime,
  driverHaltingTime: stepsOrderPoint.driverHaltingTime,
  driverTime: stepsOrderPoint.driverTime,
  haltingTime: stepsOrderPoint.haltingTime,
  lat: stepsOrderPoint.lat,
  lon: stepsOrderPoint.lon,
  time: stepsOrderPoint.time,
});

const createDetailsItemStepsOrderPoints = (
  stepsOrderPoints: OrderOptimizerDetailsOtherStepsOrdersPoints[],
): OrderOptimizerTaxiDetailsItemStepsOrdersPoints[] => {
  return stepsOrderPoints.map(createDetailsItemStepsOrderPoint);
};

const createDetailsItemStepsOrder = (
  stepsOrder: OrderOptimizerDetailsOtherStepsOrders,
): OrderOptimizerTaxiDetailsItemStepsOrders => ({
  distance: stepsOrder.distance,
  haltingTime: stepsOrder.haltingTime,
  points: createDetailsItemStepsOrderPoints(stepsOrder.points),
  totalTime: stepsOrder.totalTime,
});

const createDetailsItemStepsOrders = (
  stepsOrders: OrderOptimizerDetailsOtherStepsOrders[],
): OrderOptimizerTaxiDetailsItemStepsOrders[] => {
  return stepsOrders.map(createDetailsItemStepsOrder);
};

const createDetailsItemStepsHomeToStart = (
  stepsHomeToStart: OrderOptimizerDetailsOtherStepsHomeToStart | null,
): OrderOptimizerTaxiDetailsItemStepsHomeToStart | null => {
  if (!stepsHomeToStart) return null;
  return {
    displayName: stepsHomeToStart.displayName,
    driverTime: stepsHomeToStart.driverTime,
  };
};

const createDetailsItemStepsEndToHome = (
  stepsEndToHome: OrderOptimizerDetailsOtherStepsEndToHome | null,
): OrderOptimizerTaxiDetailsItemStepsEndToHome | null => {
  if (!stepsEndToHome) return null;
  return {
    displayName: stepsEndToHome.displayName,
    driverDistance: stepsEndToHome.driverDistance,
    driverDrivingTime: stepsEndToHome.driverDrivingTime,
    driverTime: stepsEndToHome.driverTime,
  };
};

const createDetailsItemSteps = (
  steps: OrderOptimizerDetailsOtherSteps,
): OrderOptimizerTaxiDetailsItemSteps => {
  return {
    endToHome: createDetailsItemStepsEndToHome(steps.endToHome),
    homeToStart: createDetailsItemStepsHomeToStart(steps.homeToStart),
    orders: createDetailsItemStepsOrders(steps.orders),
  };
};

const createDetailsItemOrdersBySabs = (
  ordersBySabs: OrderOptimizerDetailsOtherOrdersByDriverSabs | null,
): OrderOptimizerTaxiDetailsItemOrdersByDriverSabs | null => {
  if (!ordersBySabs) return null;
  return {
    distance: ordersBySabs.distance,
    haltingTime: ordersBySabs.haltingTime,
    totalTime: ordersBySabs.totalTime,
  };
};

const createDetailsItemOrdersByAba = (
  ordersByABa: OrderOptimizerDetailsOtherOrdersByDriverAba | null,
): OrderOptimizerTaxiDetailsItemOrdersByDriverAba | null => {
  if (!ordersByABa) return null;
  return {
    distance: ordersByABa.distance,
    haltingTime: ordersByABa.haltingTime,
    totalTime: ordersByABa.totalTime,
  };
};

const createDetailsItemOrder = (
  order: OrderOptimizerDetailsOtherOrders,
): OrderOptimizerTaxiDetailsItemOrders => ({
  billingModel: order.billingModel,
  cargoCompany: order.cargoCompany,
  distance: order.distance,
  driverName: order.driverName,
  fleetPartner: order.fleetPartner,
  haltingTime: order.haltingTime,
  humanId: order.humanId,
  planEntryHumanId: order.planEntryHumanId,
  startTime: order.startTime,
  taxiCorporation: order.taxiCorporation,
  totalTime: order.totalTime,
});

const createDetailsItemOrders = (
  orders: OrderOptimizerTaxiDetailsItemOrders[],
): OrderOptimizerDetailsOtherOrders[] => {
  return orders.map(createDetailsItemOrder);
};

const createDetailsItemOptimizedStats = (
  optimizedStats: OrderOptimizerDetailsOtherOptimizedStats,
): OrderOptimizerTaxiDetailsItemOptimizedStats => {
  return {
    contractRate: optimizedStats.contractRate,
    distance: optimizedStats.distance,
    haltingTime: optimizedStats.haltingTime,
    margin: optimizedStats.margin,
    totalTime: optimizedStats.totalTime,
  };
};

const createDetailsItemUnoptimizedStats = (
  unoptimizedStats: OrderOptimizerDetailsOtherUnoptimizedStats,
): OrderOptimizerTaxiDetailsItemUnoptimizedStats => {
  return {
    contractRate: unoptimizedStats.contractRate,
    distance: unoptimizedStats.distance,
    haltingTime: unoptimizedStats.haltingTime,
    margin: unoptimizedStats.margin,
    totalTime: unoptimizedStats.totalTime,
  };
};

const createDetailsItemDriver = (
  driver: OrderOptimizerDetailsOtherDriver | null,
): OrderOptimizerTaxiDetailsItemDriver | null => {
  if (!driver) return null;
  return {
    contractRate: driver.contractRate,
    displayName: driver.displayName,
    driverId: driver.driverId,
    startLat: driver.startLat,
    startLon: driver.startLon,
    taxiCorporation: driver.taxiCorporation,
  };
};

const createStatus = (
  status: OrderOptimizerDetailsOtherStatus,
): OrderOptimizerTaxiDetailsItemStatus => {
  switch (status) {
    case OrderOptimizerDetailsOtherStatus.INTERESTING:
      return OrderOptimizerTaxiDetailsItemStatus.INTERESTING;
    case OrderOptimizerDetailsOtherStatus.PENDING:
      return OrderOptimizerTaxiDetailsItemStatus.PENDING;
    case OrderOptimizerDetailsOtherStatus.REJECTED:
      return OrderOptimizerTaxiDetailsItemStatus.REJECTED;
  }
};

const createDetailsItem = (
  detailsItem: OrderOptimizerDetailsOther,
): OrderOptimizerTaxiDetailsItem => {
  return {
    driver: createDetailsItemDriver(detailsItem.driver),
    optimizedStats: createDetailsItemOptimizedStats(detailsItem.optimizedStats),
    orders: createDetailsItemOrders(detailsItem.orders),
    ordersByDriverAba: createDetailsItemOrdersByAba(
      detailsItem.ordersByDriverAba,
    ),
    ordersByDriverSabs: createDetailsItemOrdersBySabs(
      detailsItem.ordersByDriverSabs,
    ),
    reviewedAt: detailsItem.reviewedAt,
    reviewedBy: detailsItem.reviewedBy,
    saAndBsDistance: detailsItem.saAndBsDistance,
    steps: createDetailsItemSteps(detailsItem.steps),
    unoptimizedStats: createDetailsItemUnoptimizedStats(
      detailsItem.unoptimizedStats,
    ),
    score: detailsItem.score,
    status: createStatus(detailsItem.status),
    totalCargoIncome: detailsItem.totalCargoIncome,
    totalDriverHaltingCost: detailsItem.totalDriverHaltingCost,
  };
};

const orderOptimizerTaxiDetailsItemFactory = {
  createDetailsItem,
  createDetailsItemOrders,
};

export default orderOptimizerTaxiDetailsItemFactory;
